import { useEffect } from "react";
import { useHeroData } from "../../hooks/useHeroData";
import { useProjects } from "../../hooks/useProjects";
import { useExperiences } from "../../hooks/useExperiences";
import { useSkills } from "../../hooks/useSkills";

const SEO_DEFAULTS = {
    title: "Carlos Oliva (Colidom) - Desarrollador Full Stack | Portfolio Profesional",
    description:
        "Portfolio profesional de Carlos Oliva (Colidom), desarrollador full stack especializado en React, Node.js, Python, FastAPI y tecnologías modernas. Explora mis proyectos, experiencia profesional y habilidades técnicas.",
    keywords:
        "Carlos Oliva, Colidom, desarrollador full stack, portfolio desarrollador, react developer, node.js developer, python developer, fastapi, javascript, typescript, desarrollo web, programador freelance, diseño web responsive, frontend developer, backend developer, full stack engineer, portfolio profesional, proyectos web, desarrollo aplicaciones",
    author: "Carlos Oliva",
    siteUrl: "https://colidom.vercel.app",
    image: "/og-image.png",
    type: "website",
    locale: "es_ES",
    twitterHandle: "@colidom",
};

export default function SEO({ title, description, keywords, image, url, type, author }) {
    const { heroData } = useHeroData();
    const { projectsData } = useProjects();
    const { experiences } = useExperiences();
    const { skills } = useSkills();

    const seoTitle = title ? `${title} | ${SEO_DEFAULTS.title}` : SEO_DEFAULTS.title;
    const seoDescription = description || SEO_DEFAULTS.description;
    const seoKeywords = keywords || SEO_DEFAULTS.keywords;
    const seoImage = image || `${SEO_DEFAULTS.siteUrl}${SEO_DEFAULTS.image}`;
    const seoUrl = url || SEO_DEFAULTS.siteUrl;
    const seoAuthor = author || SEO_DEFAULTS.author;
    const seoType = type || SEO_DEFAULTS.type;

    useEffect(() => {
        // Update document title
        document.title = seoTitle;

        // Update or create meta tags
        const updateMetaTag = (name, content, property = false) => {
            if (!content) return; // Skip if no content
            const attribute = property ? "property" : "name";
            let element = document.querySelector(`meta[${attribute}="${name}"]`);

            if (!element) {
                element = document.createElement("meta");
                element.setAttribute(attribute, name);
                document.head.appendChild(element);
            }
            element.setAttribute("content", content);
        };

        // Update or create link tags
        const updateLinkTag = (rel, href, sizes = null) => {
            let element = document.querySelector(`link[rel="${rel}"]${sizes ? `[sizes="${sizes}"]` : ''}`);
            if (!element) {
                element = document.createElement("link");
                element.setAttribute("rel", rel);
                if (sizes) element.setAttribute("sizes", sizes);
                document.head.appendChild(element);
            }
            element.setAttribute("href", href);
        };

        // Primary Meta Tags
        updateMetaTag("description", seoDescription);
        updateMetaTag("keywords", seoKeywords);
        updateMetaTag("author", seoAuthor);
        updateMetaTag("robots", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
        updateMetaTag("googlebot", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");
        updateMetaTag("bingbot", "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1");

        // Language and geo tags
        updateMetaTag("language", "Spanish");
        updateMetaTag("geo.region", "ES");

        // Mobile optimization
        updateMetaTag("mobile-web-app-capable", "yes");
        updateMetaTag("apple-mobile-web-app-capable", "yes");
        updateMetaTag("apple-mobile-web-app-status-bar-style", "black-translucent");
        updateMetaTag("apple-mobile-web-app-title", "Colidom Portfolio");

        // Open Graph / Facebook
        updateMetaTag("og:type", seoType, true);
        updateMetaTag("og:url", seoUrl, true);
        updateMetaTag("og:title", seoTitle, true);
        updateMetaTag("og:description", seoDescription, true);
        updateMetaTag("og:image", seoImage, true);
        updateMetaTag("og:image:width", "1200", true);
        updateMetaTag("og:image:height", "630", true);
        updateMetaTag("og:image:alt", "Carlos Oliva - Desarrollador Full Stack Portfolio", true);
        updateMetaTag("og:locale", SEO_DEFAULTS.locale, true);
        updateMetaTag("og:site_name", "Portfolio Carlos Oliva (Colidom)", true);

        // Twitter
        updateMetaTag("twitter:card", "summary_large_image");
        updateMetaTag("twitter:url", seoUrl);
        updateMetaTag("twitter:title", seoTitle);
        updateMetaTag("twitter:description", seoDescription);
        updateMetaTag("twitter:image", seoImage);
        updateMetaTag("twitter:image:alt", "Carlos Oliva - Desarrollador Full Stack Portfolio");
        updateMetaTag("twitter:creator", SEO_DEFAULTS.twitterHandle);
        updateMetaTag("twitter:site", SEO_DEFAULTS.twitterHandle);

        // Update canonical link
        updateLinkTag("canonical", seoUrl);

        // Preconnect to external domains for performance
        updateLinkTag("preconnect", "https://fonts.googleapis.com");
        updateLinkTag("preconnect", "https://fonts.gstatic.com");
        updateLinkTag("dns-prefetch", "https://www.google-analytics.com");

        // Update theme color
        let themeColor = document.querySelector('meta[name="theme-color"]');
        if (!themeColor) {
            themeColor = document.createElement("meta");
            themeColor.setAttribute("name", "theme-color");
            document.head.appendChild(themeColor);
        }
        themeColor.setAttribute("content", "#1F2937");

        // Structured Data (JSON-LD) - Person Schema
        const personData = {
            "@context": "https://schema.org",
            "@type": "Person",
            "@id": `${SEO_DEFAULTS.siteUrl}/#person`,
            name: heroData?.name || "Carlos Oliva",
            alternateName: "Colidom",
            jobTitle: heroData?.title || "Desarrollador Full Stack",
            description: heroData?.description || seoDescription,
            url: seoUrl,
            image: seoImage,
            sameAs: [
                "https://github.com/colidom",
                "https://linkedin.com/in/colidom",
                SEO_DEFAULTS.siteUrl
            ],
            knowsAbout: skills?.slice(0, 20).map(skill => skill.name) || [
                "React",
                "Node.js",
                "JavaScript",
                "TypeScript",
                "Python",
                "FastAPI",
                "PostgreSQL",
                "MongoDB",
                "Docker",
                "Git",
                "Desarrollo Web",
                "Full Stack Development"
            ],
            alumniOf: {
                "@type": "Organization",
                name: "Universidad"
            }
        };

        // Structured Data - WebSite Schema
        const websiteData = {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "@id": `${SEO_DEFAULTS.siteUrl}/#website`,
            url: seoUrl,
            name: "Portfolio Carlos Oliva (Colidom)",
            description: seoDescription,
            publisher: {
                "@id": `${SEO_DEFAULTS.siteUrl}/#person`
            },
            inLanguage: "es-ES"
        };

        // Structured Data - ProfilePage Schema
        const profilePageData = {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            dateCreated: "2024-01-01",
            dateModified: new Date().toISOString().split('T')[0],
            mainEntity: {
                "@id": `${SEO_DEFAULTS.siteUrl}/#person`
            }
        };

        // Structured Data - ItemList for Projects (if available)
        const projectsListData = projectsData?.length > 0 ? {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: projectsData.slice(0, 10).map((project, index) => ({
                "@type": "ListItem",
                position: index + 1,
                item: {
                    "@type": "CreativeWork",
                    name: project.title,
                    description: project.description,
                    url: project.demo_link || project.code_link,
                    image: project.image,
                    creator: {
                        "@id": `${SEO_DEFAULTS.siteUrl}/#person`
                    },
                    keywords: project.technologies?.join(", ")
                }
            }))
        } : null;

        // Structured Data - WorkHistory (if available)
        const workHistoryData = experiences?.length > 0 ? experiences.slice(0, 5).map(exp => {
            const projects = exp.projects || [];
            return projects.map(project => ({
                "@context": "https://schema.org",
                "@type": "WorkRole",
                roleName: project.job_title,
                description: project.description,
                startDate: project.start_date,
                endDate: project.end_date || undefined,
                skills: project.technologies?.join(", "),
                worksFor: {
                    "@type": "Organization",
                    name: exp.company
                }
            }));
        }).flat() : null;

        // Combine all structured data
        const allStructuredData = {
            "@context": "https://schema.org",
            "@graph": [
                personData,
                websiteData,
                profilePageData,
                projectsListData,
                ...(workHistoryData || [])
            ].filter(Boolean)
        };

        // Add or update JSON-LD script
        let script = document.querySelector('script[type="application/ld+json"]#seo-structured-data');
        if (!script) {
            script = document.createElement("script");
            script.setAttribute("type", "application/ld+json");
            script.setAttribute("id", "seo-structured-data");
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(allStructuredData, null, 2);

        // Add breadcrumb structured data
        const breadcrumbData = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Inicio",
                    item: seoUrl
                }
            ]
        };

        let breadcrumbScript = document.querySelector('script[type="application/ld+json"]#breadcrumb-data');
        if (!breadcrumbScript) {
            breadcrumbScript = document.createElement("script");
            breadcrumbScript.setAttribute("type", "application/ld+json");
            breadcrumbScript.setAttribute("id", "breadcrumb-data");
            document.head.appendChild(breadcrumbScript);
        }
        breadcrumbScript.textContent = JSON.stringify(breadcrumbData);

    }, [seoTitle, seoDescription, seoKeywords, seoImage, seoUrl, seoAuthor, seoType, heroData, projectsData, experiences, skills]);

    return null; // Este componente no renderiza nada visual
}
